import { useCallback, useEffect } from "react";
import { Bell, Bot, CheckCheck, Crown, GraduationCap, Home, Megaphone, MessageSquare, UserSquare2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../connect/api";
import { useConnect } from "../../connect/store";
import { Empty, Header, LoadMore, ago, usePaged } from "./ui";
import { PushToggle, SoundToggle } from "./Push";

const ICON = { puppet: UserSquare2, auto_reply: Bot, group_reply: Megaphone, message: MessageSquare, ceo_message: Crown, training_material: GraduationCap, wfh_reminder: Home, wfh_auto_checkout: Home };

export const Activity = () => {
  const { refreshBadges } = useConnect();
  const nav = useNavigate();
  const fetcher = useCallback((p) => api.get("/notifications", { params: { page: p, limit: 30 } }).then((r) => r.data), []);
  const list = usePaged(fetcher, []);

  const reload = list.reload;
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    list.setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    refreshBadges();
  };

  return (
    <div className="h-full flex flex-col">
      <Header title="Activity" sub="Notifications" right={<div className="flex gap-1.5 shrink-0"><SoundToggle /><PushToggle /><button onClick={readAll} className="nxc-btn nxc-btn-ghost h-9 text-xs" data-testid="notifications-read-all" title="Mark all read"><CheckCheck size={14} /> <span className="hidden sm:inline">Read all</span></button></div>} />
      <div className="flex-1 overflow-y-auto" onScroll={list.onScroll} data-testid="notifications-list">
        {!list.loading && list.items.length === 0 && <Empty icon={Bell} title="You're all caught up" text="New messages and alerts will show up here." />}
        <ul className="divide-y divide-white/5">
          {list.items.map((n) => {
            const Icon = ICON[n.kind] || Bell;
            return (
              <li key={n.id}>
                <button onClick={() => n.link && nav(n.link)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/[0.03] transition-colors ${n.read ? "" : "bg-blue-500/[0.06]"}`} data-testid={`notification-${n.id}`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border ${n.read ? "bg-white/5 border-white/10 text-slate-400" : "bg-blue-500/15 border-blue-500/30 text-blue-300"}`}><Icon size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className={`text-sm truncate ${n.read ? "text-slate-300" : "text-white font-semibold"}`}>{n.title}</div>
                      <div className="text-[11px] text-slate-500 shrink-0">{ago(n.at)}</div>
                    </div>
                    <div className="text-xs text-slate-400 line-clamp-2 whitespace-pre-line">{n.body}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <LoadMore hasMore={list.hasMore} loading={list.loading && list.items.length > 0} onClick={list.loadMore} />
      </div>
    </div>
  );
};
